import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { OfferAPI } from '../../../api/payments/offer/api';
import { PlansAPI } from '../../../api/payments/plans/api';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { DetailRow } from '../../ClienteDados/components/Financeiro/Financial/components/DetailRow';
import '../../ClienteDados/components/Financeiro/Financial/components/FinancialActionsMenu.css';
import { PAYMENT_METHOD_LABELS } from '../../PaymentPlans/utils';
import { DetailListRow } from '../_components/DetailListRow';
import { OfferStatusBadge } from '../_components/OfferStatusBadge';
import { OrderSummaryCard } from '../_components/OrderSummaryCard';
import { PlanDetailsPanel } from '../_components/PlanDetailsPanel';
import { JdsButton } from '../../../components/jds/JdsButton';
import '../../../components/jds/jds.css';
import { copyOfferLink, formatExpiresAt } from '../utils';

async function loadPlan(offer) {
  if (!offer?.planName) return null;
  const findPlan = async (active) => {
    const { data } = await PlansAPI.list({ name: offer.planName, active });
    return (
      data?.content?.find((plan) => plan.id === offer.planId) ??
      data?.content?.find((plan) => plan.name === offer.planName)
    );
  };
  return (await findPlan(true)) ?? findPlan(false);
}

export default function OfferDetails() {
  const { id } = useParams();
  const history = useHistory();
  const [offer, setOffer] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPlan(null);
    OfferAPI.getV2(id)
      .then((response) => {
        if (cancelled) return;
        const data = response.data ?? response;
        setOffer(data);
        // O painel do plano é complementar: se falhar, a tela segue sem ele.
        loadPlan(data)
          .then((loaded) => {
            if (!cancelled) setPlan(loaded);
          })
          .catch((error) => console.error('Erro ao buscar o plano:', error));
      })
      .catch(() => {
        if (!cancelled) setOffer(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading)
    return <div className="jds-scope">Carregando dados da oferta...</div>;
  if (!offer) return <div className="jds-scope">Oferta não encontrada.</div>;

  const addons = offer.addons ?? [];
  const { coupon, customerEmail } = offer;
  const paymentMethods = (offer.allowedPaymentMethods ?? [])
    .map((method) => PAYMENT_METHOD_LABELS[method] ?? method)
    .join(', ');

  // Mesmo resumo da criação. Os totais vêm da API: o desconto é a diferença
  // entre plano + add-ons e o preço final da oferta.
  const summaryItems = addons.map((addon, index) => {
    const quantity = addon.itemCount ?? 1;
    return {
      key: addon.id ?? `${addon.itemId}-${index}`,
      itemId: addon.itemId,
      quantity,
      item: { name: addon.itemName, value: addon.total / quantity },
    };
  });
  const subtotal =
    Number(offer.planValue ?? 0) +
    addons.reduce((sum, addon) => sum + Number(addon.total ?? 0), 0);
  const discountAmount =
    offer.discountAmount != null
      ? Number(offer.discountAmount)
      : Math.max(0, subtotal - Number(offer.finalPrice ?? 0));
  const addonQuantities = Object.fromEntries(
    addons.map((addon) => [addon.itemId, addon.itemCount]),
  );

  const goToList = () => history.push('/payments/offers');

  return (
    <div className="jds-scope">
      <div className="mb-5">
        <span
          role="button"
          tabIndex={0}
          onClick={goToList}
          onKeyDown={(event) => event.key === 'Enter' && goToList()}
          className="text-muted"
          style={{ cursor: 'pointer' }}
        >
          &lt; Painel ADMIN
        </span>
        <span className="text-muted"> &bull; Ofertas</span>
      </div>

      <div
        className="d-flex align-items-start"
        style={{ gap: 'var(--space-16)' }}
      >
        <div className="jds-card" style={{ flex: '1 1 600px', minWidth: 0 }}>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ marginBottom: 'var(--space-24)', gap: 'var(--space-16)' }}
          >
            <h1
              className="jds-title-md"
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={offer.name}
            >
              {offer.name ?? 'Detalhes da oferta'}
            </h1>
            <div
              className="d-flex"
              style={{ gap: 'var(--space-8)', flexShrink: 0 }}
            >
              <JdsButton
                variant="outlined"
                color="neutral"
                disabled={!offer.codeHash}
                onClick={() => copyOfferLink(offer)}
              >
                Copiar link
              </JdsButton>
              <JdsButton
                variant="outlined"
                onClick={() => history.push(`/payments/offers/${id}/edit`)}
              >
                Editar
              </JdsButton>
            </div>
          </div>

          <div className="mb-6 d-flex flex-column" style={{ gap: 12 }}>
            <DetailRow label="Status">
              <OfferStatusBadge offer={offer} />
            </DetailRow>
            <DetailRow label="Cadastrada em">
              {offer.createdAt ? formatExpiresAt(offer.createdAt) : '-'}
            </DetailRow>
            <DetailRow label="Data de vencimento">
              {formatExpiresAt(offer.expirationDate)}
            </DetailRow>
            <DetailRow label="Métodos de pagamento">
              {paymentMethods || '-'}
            </DetailRow>
            <DetailRow label="E-mail do cliente">
              {customerEmail ?? '-'}
            </DetailRow>
            <DetailRow label="Plano base">{offer.planName ?? '-'}</DetailRow>
            <DetailRow label="Valor do plano" boldValue>
              {formatCurrency(offer.planValue)}
            </DetailRow>
          </div>

          <hr className="jds-divider" />

          <div className="mb-6">
            <h3 className="jds-title-xs mb-3">Add-ons</h3>
            {addons.length > 0 ? (
              <div className="d-flex flex-column" style={{ gap: 12 }}>
                {addons.map((addon, index) => (
                  <DetailListRow
                    key={addon.id ?? `${addon.name}-${index}`}
                    id={`offer-addon-actions-${addon.id ?? index}`}
                    labelPrimary={addon.itemName}
                    labelSecondary={`${addon.itemCount}x ${
                      addon.itemCount > 1 ? 'itens' : 'item'
                    }`}
                    value={formatCurrency(addon.total)}
                    actionLabel="Detalhes do Produto"
                    onAction={() =>
                      history.push(`/payments/items/${addon.itemId}`)
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted mb-0">Nenhum add-on cadastrado.</p>
            )}
          </div>

          <hr className="jds-divider" />

          <div className="mb-6">
            <h3 className="jds-title-xs mb-3">Cupom</h3>
            {coupon ? (
              <DetailListRow
                id="offer-coupon-actions"
                labelPrimary={coupon.name ?? coupon.code}
                actionLabel="Detalhes do cupom"
                onAction={() =>
                  history.push({
                    pathname: `/payments/coupons/${coupon.id}`,
                    state: { coupon },
                  })
                }
              />
            ) : (
              <p className="text-muted mb-0">Nenhum cupom aplicado.</p>
            )}
          </div>

          <OrderSummaryCard
            planName={offer.planName ?? ''}
            planAmount={Number(offer.planValue ?? 0)}
            items={summaryItems}
            discountAmount={discountAmount}
            couponCode={coupon?.code ?? coupon?.name}
            finalTotal={Number(offer.finalPrice ?? 0)}
          />
        </div>

        {plan && (
          <PlanDetailsPanel plan={plan} addonQuantities={addonQuantities} />
        )}
      </div>
    </div>
  );
}
